import { AlertTriangle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

// Componente per la legenda con tooltip
function LegendTooltip({ data }) {
    const [showLegend, setShowLegend] = useState(false);

    return (
        <div className="relative mt-3">
            <div 
                className="text-center cursor-help text-xs font-semibold text-gray-600 uppercase"
                onMouseEnter={() => setShowLegend(true)}
                onMouseLeave={() => setShowLegend(false)}
            >
                Legenda
            </div>
            {showLegend && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white border border-gray-300 rounded-lg shadow-xl p-3 z-50 min-w-[140px] sm:min-w-[180px] max-w-[90vw]">
                    <div className="space-y-1">
                        {data.labels.map((label, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                                <div 
                                    className="w-3 h-3 rounded-sm flex-shrink-0" 
                                    style={{ backgroundColor: data.datasets[0].backgroundColor[i] }}
                                />
                                <span className="text-gray-700 uppercase">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function VulnerabilityCharts({ vulnerabilities }) {
    
    // Calcola statistiche per tipologia di minaccia (CVE)
    const cveStats = useMemo(() => {
        const stats = {};
        vulnerabilities?.forEach(vuln => {
            const cve = vuln.cve_id || vuln.vulnerabilita || 'UNKNOWN';
            const prefix = cve.split('-')[0] || 'OTHER'; // CVE, CWE, etc.
            stats[prefix] = (stats[prefix] || 0) + 1;
        });
        return stats;
    }, [vulnerabilities]);

    // Calcola statistiche per livello di rischio
    const riskStats = useMemo(() => {
        const stats = {};
        vulnerabilities?.forEach(vuln => {
            const risk = (vuln.rischio || 'UNKNOWN').toUpperCase();
            stats[risk] = (stats[risk] || 0) + 1;
        });
        return stats;
    }, [vulnerabilities]);

    // Calcola livello di rischio NIS2 basato sui tempi di risoluzione con approccio avanzato
    const nis2ComplianceData = useMemo(() => {
        let totalVulns = 0;
        let criticalUnresolved = 0;
        let highUnresolved = 0;
        let totalRiskScore = 0;
        let resolvedCount = 0;
        let overdueCount = 0;
        let avgResolutionDays = 0;
        let totalResolutionDays = 0;
        let highestSeverity = 'low'; // Per calcolare il best value SLO

        // Policy SLO - Target di remediation in giorni
        const getTargetDays = (severity, hasExploit = false) => {
            let baseTarget;
            const sev = severity?.toLowerCase();
            
            // Base target per severità (assumiamo exposure INTERNET come default più restrittivo)
            if (sev === 'critical') baseTarget = 7;
            else if (sev === 'high') baseTarget = 30;
            else if (sev === 'medium') baseTarget = 90;
            else baseTarget = 180; // low
            
            // Accelerazione per exploit attivi (dimezziamo il target)
            // TODO: integrare campo exploit_status quando disponibile dall'API
            if (hasExploit) {
                baseTarget = Math.max(2, Math.floor(baseTarget / 2));
            }
            
            return baseTarget;
        };

        // Determina la severità più alta per il best value SLO
        const updateHighestSeverity = (severity) => {
            const sev = severity?.toLowerCase();
            const severityRank = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1, 'info': 0 };
            const currentRank = severityRank[highestSeverity] || 0;
            const newRank = severityRank[sev] || 0;
            if (newRank > currentRank) {
                highestSeverity = sev;
            }
        };

        // Calcola risk score temporale per singola CVE
        const calculateTemporalRisk = (ttr, targetDays, severity) => {
            const overdueFactor = ttr / targetDays;
            
            // Score a soglia (semplice da spiegare)
            if (overdueFactor <= 1.0) return 20; // Nei tempi
            if (overdueFactor <= 2.0) return 60; // 1-2x il target
            return 90; // >2x il target
        };

        vulnerabilities?.forEach(vuln => {
            totalVulns++;
            const status = vuln.status?.toLowerCase();
            const risk = vuln.rischio?.toLowerCase();
            const dataRilevamento = vuln.data_rilevamento;
            const lastStatusChange = vuln.last_status_change;
            
            // Aggiorna la severità più alta
            updateHighestSeverity(risk);
            
            const isResolved = status === 'closed' || status === 'resolved' || status === 'managed';
            const isCritical = risk === 'critical';
            const isHigh = risk === 'high';

            // Conta vulnerabilità critiche/high non risolte
            if (!isResolved) {
                if (isCritical) criticalUnresolved++;
                if (isHigh) highUnresolved++;
            }

            if (isResolved && dataRilevamento && lastStatusChange) {
                try {
                    let dtRilevamento, dtRisoluzione;

                    if (dataRilevamento.includes('/')) {
                        const [day, month, year] = dataRilevamento.split('/');
                        dtRilevamento = new Date(`${year}-${month}-${day}`);
                    } else if (dataRilevamento.includes('-')) {
                        dtRilevamento = new Date(dataRilevamento.split('T')[0]);
                    }

                    if (lastStatusChange.includes('T')) {
                        dtRisoluzione = new Date(lastStatusChange.split('T')[0]);
                    } else if (lastStatusChange.includes('/')) {
                        const [day, month, year] = lastStatusChange.split('/');
                        dtRisoluzione = new Date(`${year}-${month}-${day}`);
                    }

                    if (dtRilevamento && dtRisoluzione && !isNaN(dtRilevamento) && !isNaN(dtRisoluzione)) {
                        const ttr = Math.floor((dtRisoluzione - dtRilevamento) / (1000 * 60 * 60 * 24));
                        totalResolutionDays += ttr;
                        resolvedCount++;
                        
                        // Calcola target e rischio temporale
                        const targetDays = getTargetDays(risk, false); // TODO: passare hasExploit quando disponibile
                        const temporalRisk = calculateTemporalRisk(ttr, targetDays, risk);
                        totalRiskScore += temporalRisk;
                        
                        // Conta se è in ritardo
                        if (ttr > targetDays) {
                            overdueCount++;
                        }
                    }
                } catch (error) {
                    console.warn('Errore parsing date NIS2:', error, vuln);
                }
            } else if (!isResolved && dataRilevamento) {
                // CVE ancora aperta - calcola da quanto tempo
                try {
                    let dtRilevamento;
                    const now = new Date();

                    if (dataRilevamento.includes('/')) {
                        const [day, month, year] = dataRilevamento.split('/');
                        dtRilevamento = new Date(`${year}-${month}-${day}`);
                    } else if (dataRilevamento.includes('-')) {
                        dtRilevamento = new Date(dataRilevamento.split('T')[0]);
                    }

                    if (dtRilevamento && !isNaN(dtRilevamento)) {
                        const daysOpen = Math.floor((now - dtRilevamento) / (1000 * 60 * 60 * 24));
                        const targetDays = getTargetDays(risk, false);
                        
                        // Penalità per vulnerabilità aperte oltre il target
                        if (daysOpen > targetDays) {
                            const overdueFactor = daysOpen / targetDays;
                            // Score maggiorato per CVE non ancora risolte
                            const penalty = Math.min(100, 60 + 20 * overdueFactor);
                            totalRiskScore += penalty;
                            overdueCount++;
                        } else {
                            // CVE aperta ma ancora entro i tempi
                            totalRiskScore += 30;
                        }
                    }
                } catch (error) {
                    console.warn('Errore calcolo età CVE aperta:', error, vuln);
                }
            }
        });

        avgResolutionDays = resolvedCount > 0 ? Math.round(totalResolutionDays / resolvedCount) : 0;
        
        // Score medio normalizzato (0-100)
        const avgRiskScore = totalVulns > 0 ? Math.round(totalRiskScore / totalVulns) : 0;
        
        // Percentuale entro target (KPI)
        const onTimePercent = resolvedCount > 0 ? Math.round(((resolvedCount - overdueCount) / resolvedCount) * 100) : 0;

        // Determina livello di rischio finale
        let riskLevel = 'info';
        if (avgRiskScore >= 70 || onTimePercent < 50) riskLevel = 'critical';
        else if (avgRiskScore >= 50 || onTimePercent < 70) riskLevel = 'high';
        else if (avgRiskScore >= 30 || onTimePercent < 85) riskLevel = 'medium';
        else riskLevel = 'low';

        // Calcola best value (giorni SLO della severità più alta)
        const bestValue = getTargetDays(highestSeverity, false);

        return {
            score: avgRiskScore,
            riskLevel,
            criticalUnresolved,
            highUnresolved,
            avgResolutionDays,
            onTimePercent,
            overdueCount,
            totalVulns,
            bestValue
        };
    }, [vulnerabilities]);

    // Calcola statistiche per tempi di risoluzione
    const resolutionTimeStats = useMemo(() => {
        const stats = {
            '0-7 gg': 0,
            '8-30 gg': 0,
            '31-90 gg': 0,
            '>90 gg': 0,
            'Non risolte': 0
        };

        vulnerabilities?.forEach(vuln => {
            const status = vuln.status?.toLowerCase();
            const dataRilevamento = vuln.data_rilevamento;
            const lastStatusChange = vuln.last_status_change;

            // Considera risolte se status è 'closed', 'resolved', o 'managed'
            const isResolved = status === 'closed' || status === 'resolved' || status === 'managed';

            if (isResolved && dataRilevamento && lastStatusChange) {
                try {
                    let dtRilevamento, dtRisoluzione;

                    // Parse data rilevamento (formato: "26/06/2025" o "2025-06-26")
                    if (dataRilevamento.includes('/')) {
                        const [day, month, year] = dataRilevamento.split('/');
                        dtRilevamento = new Date(`${year}-${month}-${day}`);
                    } else if (dataRilevamento.includes('-')) {
                        dtRilevamento = new Date(dataRilevamento.split('T')[0]);
                    }

                    // Parse data risoluzione (formato: "2025-11-17T19:18:24.843753")
                    if (lastStatusChange.includes('T')) {
                        dtRisoluzione = new Date(lastStatusChange.split('T')[0]);
                    } else if (lastStatusChange.includes('/')) {
                        const [day, month, year] = lastStatusChange.split('/');
                        dtRisoluzione = new Date(`${year}-${month}-${day}`);
                    }

                    if (dtRilevamento && dtRisoluzione && !isNaN(dtRilevamento) && !isNaN(dtRisoluzione)) {
                        const daysDiff = Math.floor((dtRisoluzione - dtRilevamento) / (1000 * 60 * 60 * 24));

                        if (daysDiff <= 7) {
                            stats['0-7 gg']++;
                        } else if (daysDiff <= 30) {
                            stats['8-30 gg']++;
                        } else if (daysDiff <= 90) {
                            stats['31-90 gg']++;
                        } else {
                            stats['>90 gg']++;
                        }
                    }
                } catch (error) {
                    console.warn('Errore parsing date:', error, vuln);
                }
            } else {
                stats['Non risolte']++;
            }
        });

        return stats;
    }, [vulnerabilities]);

    const severityChartData = {
        labels: Object.keys(cveStats),
        datasets: [{
            data: Object.values(cveStats),
            backgroundColor: [
                '#DC6B6B', // RED muted
                '#E8A957', // AMBER muted
                '#5FA876', // GREEN muted
                '#6B8FC9', // BLUE muted
                '#8E7AB8', // PURPLE muted
            ],
            borderWidth: 1,
        }]
    };

    const riskChartData = {
        labels: Object.keys(riskStats),
        datasets: [{
            data: Object.values(riskStats),
            backgroundColor: [
                '#C75454', // CRITICAL muted
                '#D99A4E', // HIGH muted
                '#E0B565', // MEDIUM muted
                '#5FA876', // LOW muted
                '#9CA3AF', // UNKNOWN
            ],
            borderWidth: 1,
        }]
    };

    const resolutionChartData = {
        labels: Object.keys(resolutionTimeStats),
        datasets: [{
            data: Object.values(resolutionTimeStats),
            backgroundColor: [
                '#5FA876', // 0-7 giorni (verde muted)
                '#E0B565', // 8-30 giorni (giallo muted)
                '#D99A4E', // 31-90 giorni (arancione muted)
                '#C75454', // Oltre 90 giorni (rosso muted)
                '#9CA3AF', // Non risolte (grigio)
            ],
            borderWidth: 1,
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const dataset = context.dataset;
                        const total = dataset.data.reduce((a, b) => (a || 0) + (b || 0), 0);
                        if (total === 0) return `${label}: ${value}`;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        },
        layout: {
            padding: {
                top: 5,
                bottom: 5
            }
        }
    };

    // Opzioni per il terzo grafico (identiche ora)
    const resolutionChartOptions = chartOptions;

    if (!vulnerabilities || vulnerabilities.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                Nessuna vulnerabilità disponibile per generare le statistiche
            </div>
        );
    }

    return (
        <>
            {/* Grafico Livello di Rischio */}
            <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col col-span-1 sm:col-span-2 xl:col-span-1">
                <h4 className="text-center font-semibold mb-3 lg:mb-4 text-gray-800 text-sm lg:text-base">
                    Livello di Rischio
                </h4>
                <div className="flex justify-center items-center w-full flex-1">
                    <div className="w-full max-w-[280px] aspect-square">
                        <Pie data={riskChartData} options={chartOptions} />
                    </div>
                </div>
                <LegendTooltip data={riskChartData} />
            </div>

            {/* Grafico Tempi di Risoluzione -> Indicatore NIS2 */}
            <NIS2ComplianceIndicator nis2Data={nis2ComplianceData} />
        </>
    );
}

// Componente per l'indicatore di conformità NIS2
function NIS2ComplianceIndicator({ nis2Data }) {
    // Funzione per calcolare il valore del gauge (0-100)
    const getRiskValue = (level) => {
        const riskLevels = {
            'low': 20,
            'medium': 50,
            'high': 75,
            'critical': 90,
            'info': 10
        };
        return riskLevels[level?.toLowerCase()] || 0;
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 flex flex-col col-span-1 sm:col-span-2 xl:col-span-1">
            <h4 className="text-center font-semibold mb-3 lg:mb-4 text-gray-800 text-sm lg:text-base">
                Tempi Risoluzione Vulnerabilità
            </h4>
            <div className="flex flex-col items-center justify-center space-y-2 sm:space-y-3 w-full flex-1">
                <GaugeChartNIS2 value={getRiskValue(nis2Data.riskLevel)} />
                <div className="text-center w-full flex flex-col items-center">
                    <CriticalityBadgeNIS2 livello={nis2Data.riskLevel} />
                    <div className="mt-3 text-xs text-gray-600 space-y-1">
                        <div>TTR medio: {nis2Data.avgResolutionDays}gg</div>
                        <div className="text-orange-600"><AlertTriangle size={12} className="inline" /> Best value SLO: {nis2Data.bestValue}gg</div>
                    </div>
                    <div className="mt-4 text-xs bg-gray-50 rounded p-2 border border-gray-200 w-full">
                        <p className="font-semibold text-gray-700 mb-2">Target SLO per Severità:</p>
                        <table className="w-full text-left text-gray-600">
                            <tbody>
                                <tr className="border-b border-gray-300">
                                    <td className="font-semibold text-red-600">Critical</td>
                                    <td className="text-right">7 giorni</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="font-semibold text-orange-600">High</td>
                                    <td className="text-right">30 giorni</td>
                                </tr>
                                <tr className="border-b border-gray-300">
                                    <td className="font-semibold text-yellow-600">Medium</td>
                                    <td className="text-right">90 giorni</td>
                                </tr>
                                <tr>
                                    <td className="font-semibold text-green-600">Low</td>
                                    <td className="text-right">180 giorni</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Gauge Chart per NIS2
function GaugeChartNIS2({ value }) {
    const size = 120;
    const strokeWidth = 12;
    const centerX = size / 2;
    const centerY = size / 2;
    const radius = (size - strokeWidth) / 2 - 3;
    
    const angle = 180 - (value / 100) * 180;
    const angleRad = (angle * Math.PI) / 180;
    const needleX = centerX + radius * Math.cos(angleRad);
    const needleY = centerY - radius * Math.sin(angleRad);
    
    const createArc = (startAngle, endAngle) => {
        const start = (180 - startAngle) * (Math.PI / 180);
        const end = (180 - endAngle) * (Math.PI / 180);
        const x1 = centerX + radius * Math.cos(start);
        const y1 = centerY - radius * Math.sin(start);
        const x2 = centerX + radius * Math.cos(end);
        const y2 = centerY - radius * Math.sin(end);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
    };
    
    const sectors = [
        { start: 0, end: 45, color: '#22c55e' },
        { start: 45, end: 90, color: '#eab308' },
        { start: 90, end: 135, color: '#f97316' },
        { start: 135, end: 180, color: '#ef4444' }
    ];

    return (
        <div className="flex justify-center w-24 sm:w-28 lg:w-32">
            <svg width="100%" viewBox={`0 0 ${size} ${size / 2 + 15}`} preserveAspectRatio="xMidYMid meet">
                {sectors.map((sector, idx) => (
                    <path
                        key={idx}
                        d={createArc(sector.start, sector.end)}
                        fill="none"
                        stroke={sector.color}
                        strokeWidth={strokeWidth}
                        strokeLinecap="butt"
                    />
                ))}
                <line
                    x1={centerX}
                    y1={centerY}
                    x2={needleX}
                    y2={needleY}
                    stroke="#374151"
                    strokeWidth="3"
                    strokeLinecap="round"
                    style={{ transition: 'all 0.8s ease' }}
                />
                <circle cx={centerX} cy={centerY} r="6" fill="#374151" />
                <circle cx={centerX} cy={centerY} r="3" fill="#ffffff" />
            </svg>
        </div>
    );
}

// Badge per NIS2
function CriticalityBadgeNIS2({ livello }) {
    const getBadgeStyles = (level) => {
        const styles = {
            'critical': 'bg-red-100 text-red-700 border-red-200',
            'high': 'bg-orange-100 text-orange-700 border-orange-200',
            'medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            'low': 'bg-green-100 text-green-700 border-green-200',
            'info': 'bg-blue-100 text-blue-700 border-blue-200'
        };
        return styles[level?.toLowerCase()] || styles.info;
    };

    const getLabel = (level) => {
        const labels = {
            'critical': 'CRITICO',
            'high': 'ALTO',
            'medium': 'MEDIO',
            'low': 'BASSO',
            'info': 'INFO'
        };
        return labels[level?.toLowerCase()] || 'SCONOSCIUTO';
    };

    return (
        <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getBadgeStyles(livello)}`}>
            {getLabel(livello)}
        </span>
    );
}
